import { Injectable, Injector } from "@angular/core";
import { MobileService } from "./mobile.service";
import { SettingsLocalService } from "../pages/settings/settings.service";

/**
 * Service to handle communication with native mobile app regarding push notification subscription status.
 * The native app needs to know whether the user has enabled or disabled push notifications
 * so it can register or unregister the FCM token accordingly.
 */
@Injectable({
  providedIn: "root",
})
export class NativePushNotificationService {
  private _settingsService: SettingsLocalService | null = null;

  constructor(
    private mobileService: MobileService,
    private injector: Injector
  ) {}

  /**
   * Get settings service using Injector to avoid circular dependency
   */
  private getSettingsService(): SettingsLocalService | null {
    if (!this._settingsService) {
      try {
        this._settingsService = this.injector.get(SettingsLocalService);
      } catch (error) {
        console.error(
          "[NativePushNotification] Error getting SettingsLocalService:",
          error
        );
        return null;
      }
    }
    return this._settingsService;
  }

  /**
   * Check if the web app is running in the mobile app
   */
  isRunningInMobileApp(): boolean {
    const platform = this.mobileService.getPlatformType();
    return (
      platform === this.mobileService.ANDROID ||
      platform === this.mobileService.IOS ||
      typeof (window as any).ReactNativeWebView !== "undefined"
    );
  }

  /**
   * Get the current push notification subscription status for THIS device.
   * Returns true only if THIS device (iOS/Android) has an active subscription in the database.
   * Uses device-specific subscription status, NOT global preferences.
   * Returns false if settings are not loaded yet or device is not subscribed.
   */
  getPushNotificationSubscriptionStatus(): boolean {
    // Get settings service using Injector to avoid circular dependency
    const settingsService = this.getSettingsService();

    if (!settingsService) {
      return false;
    }

    // Check if settings have been loaded (ServiceLoaded property)
    if (!settingsService.ServiceLoaded) {
      return false;
    }

    try {
      const settings = settingsService.getSettings();

      // CRITICAL: Use device-specific subscription status, NOT global preferences
      // Global preferences (isBrainScienceBitePushOn) are shared across all devices
      // Device-specific status tells us if THIS device is actually subscribed
      const isDeviceSubscribed = (settings as any).isDeviceSubscribedForMobilePush ?? false;


      // Return device-specific subscription status
      // Only return true if THIS device has an active subscription
      return isDeviceSubscribed;
    } catch (error) {
      console.error(
        "[NativePushNotification] Error getting subscription status:",
        error
      );
      return false;
    }
  }

  /**
   * Send the current push notification subscription status to the native app.
   * This should be called when:
   * - User toggles push notifications ON or OFF in settings
   * - App loads and detects it's running in a mobile app (to sync initial state)
   * - After fetching the user's subscription preference from the backend
   */
  sendSubscriptionStatusToNative(): void {

    if (!this.isRunningInMobileApp()) {
      return;
    }

    const isSubscribed = this.getPushNotificationSubscriptionStatus();

    // Check if the native app function is available
    const sendFunction = (window as any).sendPushNotificationSubscriptionStatus;
    if (typeof sendFunction !== "function") {
      console.warn(
        "[NativePushNotification] ⚠️ sendPushNotificationSubscriptionStatus function not available"
      );
      console.warn("[NativePushNotification] Native app may not be ready yet");
      return;
    }

    try {
      sendFunction(isSubscribed);
    } catch (error) {
      console.error(
        "[NativePushNotification] ❌ Error calling sendPushNotificationSubscriptionStatus:",
        error
      );
    }
  }

  /**
   * Handle callback from native app when push notification subscription completes.
   * This is called by the native app after it successfully subscribes/unsubscribes to push notifications.
   * Refreshes settings from the database to get the updated subscription status,
   * which will automatically update the UI (including debug sections).
   */
  onPushNotificationSubscriptionComplete(): void {

    const settingsService = this.getSettingsService();
    if (settingsService) {
      // Refresh settings from database - this will trigger UI updates via settings component subscription
      settingsService.refreshSettingsFromDb();
    } else {
      console.warn(
        "[NativePushNotification] Settings service not available, cannot refresh"
      );
    }
  }

  /**
   * Setup communication with native app.
   * This should be called during app initialization.
   */
  setupNativeCommunication(): void {

    if (!this.isRunningInMobileApp()) {
      return;
    }


    // Set up event listener FIRST (before overriding function)
    // This ensures we catch the event even if function override happens later
    window.addEventListener(
      "nativeRequestPushNotificationStatus",
      () => {
        this.sendSubscriptionStatusToNative();
      },
      { once: false } // Important: don't use once:true, we might get multiple requests
    );

    // Override the function that native app will call to request subscription status
    // This MUST override the native app's default function
    (window as any).requestNativePushNotificationStatus = () => {
      this.sendSubscriptionStatusToNative();
    };

    // Set up callback function for when native app completes subscription
    // Native app should call: window.onPushNotificationSubscriptionComplete()
    (window as any).onPushNotificationSubscriptionComplete = () => {
      this.onPushNotificationSubscriptionComplete();
    };


    // Send initial status after a short delay to ensure native app functions are injected
    setTimeout(() => {
      this.sendSubscriptionStatusToNative();
    }, 500);
  }
}
